import { signal, computed } from '@preact/signals';
import { useEffect } from 'preact/hooks';

// Dashboard metrics interface
export interface DashboardMetrics {
  totalCandidates: number;
  activeJobs: number;
  applicationsToday: number;
  avgTimeToHire: number;
  trends: {
    candidates: number[];
    applications: number[];
    interviews: number[];
  };
}

// Dashboard state signals
export const dashboardMetrics = signal<DashboardMetrics>({
  totalCandidates: 0,
  activeJobs: 0,
  applicationsToday: 0,
  avgTimeToHire: 0,
  trends: {
    candidates: [],
    applications: [],
    interviews: []
  }
});

export const isRefreshing = signal(false);
export const lastUpdate = signal<Date>(new Date());
export const refreshInterval = signal(30000); // 30 seconds

// Computed values
export const formattedLastUpdate = computed(() => {
  const now = new Date();
  const diff = now.getTime() - lastUpdate.value.getTime();
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 1) return 'Just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hour ago';
  return `${hours} hours ago`;
});

export const dashboardStatus = computed(() => {
  const errors = Object.values(dashboardMetrics.value).some(v => v === null || v === undefined);
  if (errors) return 'error';
  if (isRefreshing.value) return 'loading';
  return 'ready';
});

// Dashboard state management component
export function DashboardState({ 
  initialMetrics,
  apiEndpoint = '/api/analytics/dashboard',
  autoRefresh = true 
}: {
  initialMetrics: DashboardMetrics;
  apiEndpoint?: string;
  autoRefresh?: boolean;
}) {
  
  useEffect(() => {
    // Initialize with server data
    dashboardMetrics.value = initialMetrics;
    lastUpdate.value = new Date();
  }, [initialMetrics]);
  
  useEffect(() => {
    if (!autoRefresh) return;
    
    const refreshMetrics = async () => {
      if (isRefreshing.value) return; // Prevent overlapping requests
      
      isRefreshing.value = true;
      
      try {
        const response = await fetch(apiEndpoint);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const newMetrics: DashboardMetrics = await response.json();
        dashboardMetrics.value = newMetrics;
        lastUpdate.value = new Date();
        
      } catch (error) {
        console.error('Error refreshing dashboard metrics:', error);
        // Could emit error event or update error state here
      } finally {
        isRefreshing.value = false;
      }
    };
    
    // Set up interval for periodic updates
    const interval = setInterval(refreshMetrics, refreshInterval.value);
    
    // Clean up on unmount
    return () => clearInterval(interval);
  }, [apiEndpoint, autoRefresh]);
  
  // Manual refresh function
  const refresh = async () => {
    if (isRefreshing.value) return;
    
    isRefreshing.value = true;
    try {
      const response = await fetch(apiEndpoint);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const newMetrics: DashboardMetrics = await response.json();
      dashboardMetrics.value = newMetrics;
      lastUpdate.value = new Date();
    } catch (error) {
      console.error('Error refreshing dashboard metrics:', error);
    } finally {
      isRefreshing.value = false;
    }
  };
  
  // WebSocket connection for real-time updates (optional)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Check if WebSocket is available and create connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/dashboard`;
    
    let ws: WebSocket;
    
    try {
      ws = new WebSocket(wsUrl);
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'metrics_update') {
            dashboardMetrics.value = { ...dashboardMetrics.value, ...data.payload };
            lastUpdate.value = new Date();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.warn('WebSocket error:', error);
      };
      
    } catch (error) {
      console.warn('WebSocket not available:', error);
    }
    
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, []);
  
  // Expose refresh function globally for manual triggers
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshDashboard = refresh;
    }
  }, []);
  
  return (
    <div class="dashboard-state-info bg-gray-50 border-b px-4 py-2 flex items-center justify-between text-sm text-gray-600">
      <div class="flex items-center gap-4">
        <span>Last updated: {formattedLastUpdate.value}</span>
        <span class={`status-indicator ${dashboardStatus.value}`}>
          {dashboardStatus.value === 'loading' && '🔄'}
          {dashboardStatus.value === 'ready' && '✅'}
          {dashboardStatus.value === 'error' && '❌'}
          {dashboardStatus.value.charAt(0).toUpperCase() + dashboardStatus.value.slice(1)}
        </span>
      </div>
      
      <button 
        onClick={refresh}
        disabled={isRefreshing.value}
        class="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
      >
        {isRefreshing.value ? 'Refreshing...' : 'Refresh'}
      </button>
    </div>
  );
}